import { useEffect, useRef, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { callLSQLapp, encryptBody, getLsqLeadBody } from "../services/lsq.js";

import {
  ctaClickEvent,
  formAbandonmentEvent,
  formFieldErrorEvent,
  formIntermediateEvent,
  formStartEvent,
  formSubmissionFailEvent,
  formSuccessEvent,
} from "../services/adobeDataLayer.js";

import queryString from "query-string";
import { useCookies } from "react-cookie";

import "./form.css";

function FormComponent() {
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    control,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      accountType: ["Fixed Deposit"], // default selected
    },
  });

  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [timer, setTimer] = useState(60);

  const mobileValue = watch("mobile");

  //States for Pincode and City
  const [cityName, setCityName] = useState("");
  const [citydefined, setCitydefined] = useState(false);
  const [solID, setSolID] = useState("");
  const [branch, setBranch] = useState("");
  const [options, setOptions] = useState([]);

  //Store lead create payload values
  const formValuesRef = useRef({});

  //Store Fbp and Fbc from cookies
  const [cookies, setCookie] = useCookies();
  formValuesRef.current.FBClickID = cookies["_fbp"] || "";
  formValuesRef.current.FBPixelID = cookies["_fbc"] || "";

  const hasTriggeredFormStart = useRef(false);
  let lastAccessedField = "na";

  function showToast(type, message) {
    const toast = document.createElement("div");
    toast.className = `toast ${type}`;
    toast.textContent = message;

    document.getElementById("toastContainer").appendChild(toast);

    setTimeout(() => {
      toast.remove();
    }, 3000);
  }

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      //console.log("lead form started: ", hasTriggeredFormStart.current)
      // Custom logic to be executed before tab or window is closed
      if (hasTriggeredFormStart.current) {
        formAbandonmentEvent(
          "Senior Citizen lead form",
          "lead generation",
          lastAccessedField,
          "details",
        );
        //console.table(window.adobeDataLayer)
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    //Clean Up
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // Timer countdown
  /* OTP Timer */
  useEffect(() => {
    if (!otpSent || otpVerified || timer === 0) return;

    const interval = setInterval(() => {
      setTimer((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [otpSent, otpVerified, timer]);

  const handleSendOtp = async () => {
    ctaClickEvent(`otp:${otpSent}`, "lead form", "button", 0, "na");
    if (!mobileValue) return;
    if (mobileValue.length === 10) {
      try {
        let payload = { mobile: mobileValue, messageType: "otpGeneral" };
        let param = { otp: "send" };
        const res = await callLSQLapp(payload, "OTP", param);
        if (res.data.message.response.sent) {
          formIntermediateEvent("receive", "otp");
          showToast("success", "An OTP has been sent to your Mobile number.");
          setOtpVerified(false);
          setTimer(60);
          setOtpSent(true);
        }
      } catch (error) {
        showToast("error", "Error occurred while generating OTP.");
      }
    }
  };

  const handleVerifyOtp = async () => {
    ctaClickEvent("otp:verify", "lead form", "button", 0, "na");
    if (!mobileValue) {
      showToast("error", "Please enter mobile number");
      return;
    }
    if (otp.length === 6) {
      try {
        let payload = { mobile: mobileValue, otp: otp };
        let param = { otp: "verify" };
        const res = await callLSQLapp(payload, "OTP", param);
        if (res.data.message === "Failure Occured, please check logs") {
          showToast("error", "Please enter valid OTP");
        } else {
          formIntermediateEvent("verified", "otp");
          setOtpVerified(true);
          setTimer(0);
          showToast("success", "OTP verified.");

          formValuesRef.current.token =
            res.data.message.response.dataSent.UniqueID;
        }
      } catch (error) {
        showToast("error", "Error verifying OTP.");
      }
    }
  };

  const handlePincode = async (e) => {
    const pin = e.target.value;

    if (pin?.length !== 6) {
      // reset when not 6 digits yet
      setCityName("");
      setCitydefined(false);
      setOptions([]);
      setSolID("");
      return;
    }

    try {
      setCityName("");
      const payload = { pincode: pin };
      const param = { type: "SeniorCitizenDista" };

      const response = await callLSQLapp(payload, "MAVIS", param);
      console.log("Response from Mavis: ", response);

      // 👉 Adjusted to your actual API shape:
      const { Data = [], Count = 0 } = response?.data?.message ?? {};

      if (Count > 0 && Array.isArray(Data)) {
        // Update city name from first record (if present)
        const first = Data[0];
        const city = first?.City || "";
        setCityName(city);
        setCitydefined(Boolean(city));
        // Populate branches
        setOptions(Data);
        // Clear previous selection
        setSolID("");
      } else {
        // No branches found
        setCityName("City not found");
        setCitydefined(false);
        setOptions([]);
        setSolID("");
      }
    } catch (err) {
      console.error("Error fetching branches:", err);
      setCityName("City not found");
      setCitydefined(false);
      setOptions([]);
      setSolID("");
    }
  };

  const handleBranch = (e) => {
    const solID = e.target.value; // Sol_ID
    setSolID(solID);

    // If you want branch name:
    const selectedBranch = options.find((o) => o.Sol_ID === solID);

    if (selectedBranch) {
      const branchName = selectedBranch.Branch_Name;
      setBranch(branchName);
      console.log("Branch Name:", branchName);
      console.log("Sol ID:", solID);
    }
  };

  const handleFormStart = () => {
    if (!hasTriggeredFormStart.current) {
      formStartEvent("HL NCA lead form", "lead generation", "details");
      hasTriggeredFormStart.current = true;
    }
  };

  const onError = (errors) => {
    const simpleErrors = Object.fromEntries(
      Object.entries(errors).map(([key, value]) => [key, value.message]),
    );
    formFieldErrorEvent(simpleErrors);
    console.log("Simplified errors:", simpleErrors);
  };

  const toAccountTypeString = (values) => {
    const hasFixed = values?.includes("Fixed Deposit");
    const hasSavings = values?.includes("Savings Account");

    if (hasFixed && hasSavings) return "Both Fixed Deposit and Savings Account";
    if (hasFixed) return "Fixed Deposit";
    if (hasSavings) return "Savings Account";
    return ""; // or undefined/null if you want to enforce a selection
  };

  const onSubmit = async (data) => {
    if (!otpSent && mobileValue.length === 10) {
      showToast("error", "Click on Send OTP to continue.");
      return;
    } else if (!otp) {
      showToast("error", "Please enter OTP to continue.");
      return;
    } else if (!otpVerified) {
      showToast("error", "Please verify OTP to continue.");
      return;
    }

    if (!citydefined) {
      showToast("error", "Please enter valid pincode.");
    }

    formValuesRef.current.fullname = data.fullName;
    formValuesRef.current.mobile = data.mobile;
    formValuesRef.current.pan = data.pan;
    formValuesRef.current.pincode = data.pincode;
    formValuesRef.current.accountType = toAccountTypeString(data.accountType);
    formValuesRef.current.CityName = cityName;
    formValuesRef.current.solID = solID;
    formValuesRef.current.branch = branch;

    const utmParamsObj = queryString.parse(window.location.search);
    for (const [key, value] of Object.entries(utmParamsObj)) {
      if (key !== "campaign_code") {
        formValuesRef.current[key] = value;
      }
    }

    formValuesRef.current.CampaignId = "2765180";
    formValuesRef.current.ApiProductName = "Senior Citizen Savings Account";

    formValuesRef.current.ParentProduct = "";
    formValuesRef.current.ModeOfLead = "landing-page";

    if (data.agreetnc === true) {
      formValuesRef.current.DND = "Yes";
      formValuesRef.current.terms_chk = true;
    } else {
      formValuesRef.current.DND = "No";
      formValuesRef.current.terms_chk = false;
    }

    formValuesRef.current.IsEligible = "Yes";
    formValuesRef.current.EligibilityReason = "";
    formValuesRef.current.CompleteUrl = window.location.href;
    formValuesRef.current.User_Agent = navigator.userAgent;

    console.log("Final Submitted Data:", formValuesRef);

    let payload = {
      mobile: data.mobile,
      otp: otp,
      formdata: getLsqLeadBody(formValuesRef.current),
    };
    console.log("Lead Create Payload", payload);

    //Encrypt Payload
    const encryptedPayload = await encryptBody(payload);
    // console.log("Lead Create encrypted Payload", encryptedPayload);

    let param = {};
    let createLeadResp = await callLSQLapp(encryptedPayload, "CREATE", param);
    if (createLeadResp.data.message !== "Failure occured , please check logs") {
      formSuccessEvent(
        "Senior Citizen lead form",
        { otp: otp, pincode: data.pincode, city: cityName },
        "lead generation",
        "thank you",
        true,
      );
      let leadid = createLeadResp.data.message.Data.LeadApiCalls.Message.Id;
      console.log("leadID", leadid);
      navigate(`/thankyou?id=${leadid}`);
    } else {
      formSubmissionFailEvent();
      console.log("lead not created.");
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      onChange={handleFormStart}
      id="myForm"
    >
      <h3>Call Request form</h3>

      <div className="form_wrap  name-wrap">
        {/* Full Name */}
        <div className="input_wrapper">
          <input
            type="text"
            id="name"
            placeholder="Enter Full Name"
            // disabled={otpVerified}
            {...register("fullName", {
              required: "Full name is required",
              pattern: {
                value: /^[a-zA-Z\s]+$/,
                message: "Only alphabets and spaces allowed",
              },
            })}
            onInput={(e) => {
              e.target.value = e.target.value.replace(/[^a-zA-Z\s]/g, "");
            }}
          />
          {errors.fullName && (
            <span className="error">{errors.fullName.message}</span>
          )}
        </div>

        {/* Mobile */}
        <div className="otp_wrapper input_wrapper">
          <div className="d-flex gap-2">
            <input
              type="text"
              placeholder="Enter Mobile Number"
              disabled={otpVerified}
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={10}
              {...register("mobile", {
                required: "Mobile number is required",
                pattern: {
                  value: /^\d{10}$/,
                  message: "Enter valid 10-digit mobile number",
                },
              })}
              onInput={(e) => {
                e.target.value = e.target.value.replace(/\D/g, "");
              }}
            />
            {!otpVerified && !otpSent && (
              <button
                type="button"
                className="resend btn_abs"
                onClick={handleSendOtp}
              >
                Send OTP
              </button>
            )}
            {otpSent && timer <= 0 && (
              <button
                type="button"
                className="resend btn_abs"
                onClick={handleSendOtp}
                disabled={otpVerified}
              >
                {otpVerified ? `Verified` : `Resend OTP`}
              </button>
            )}
          </div>
          {errors.mobile && (
            <span className="error">{errors.mobile.message}</span>
          )}
        </div>

        {/* OTP */}
        {otpSent && !otpVerified && timer > 0 && (
          <div className="otp_wrapper input_wrapper">
            <div className="d-flex align-items-center gap-2">
              <input
                type="text"
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                maxLength={6}
                inputMode="numeric"
              />
              <span className="timer">
                00:{timer < 10 ? `0${timer}` : timer}
              </span>
              <button
                type="button"
                className="resend btn_abs1"
                onClick={handleVerifyOtp}
              >
                Verify OTP
              </button>
            </div>
            {errors.otp && <span className="error">{errors.otp.message}</span>}
          </div>
        )}

        {/* PAN */}
        <div className="input_wrapper">
          <input
            type="text"
            id="pan"
            placeholder="PAN Number"
            maxLength={10}
            {...register("pan", {
              required: "PAN number is required",
              pattern: {
                value: /^[A-Z]{5}\d{4}[A-Z]$/,
                message: "Invalid PAN format",
              },
            })}
            onInput={(e) => {
              e.target.value = e.target.value.toUpperCase();
            }}
          />

          {errors.pan && <span className="error">{errors.pan.message}</span>}
        </div>

        {/* Pin Code */}
        <div className="input_wrapper">
          <input
            type="text"
            id="pincode"
            placeholder="Pin code"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            {...register("pincode", {
              required: "Property PIN code is required",
              pattern: {
                value: /^\d{6}$/,
                message: "Enter valid 6-digit PIN Code number",
              },
            })}
            onInput={(e) => {
              e.target.value = e.target.value.replace(/\D/g, "");
            }}
            onChange={handlePincode}
          />

          {errors.pincode && (
            <span className="error">{errors.pincode.message}</span>
          )}
        </div>

        {/* Branch */}
        {options.length > 0 && (
          <div className="input_wrapper">
            <select
              id="branch"
              className=""
              {...register("branch", {
                required: "Select Branch",
              })}
              onChange={handleBranch}
              defaultValue=""
            >
              <option value="" disabled>
                Select a branch
              </option>

              {options.map((option) => (
                <option key={option.Sol_ID} value={option.Sol_ID}>
                  {option.Branch_Name}
                </option>
              ))}
            </select>

            {errors.branch && (
              <span className="error">{errors.branch.message}</span>
            )}
          </div>
        )}

        {/* interested in */}

        <label className="field-label d-block">I am interested in:</label>
        <div className="position-relative input_wrapper residential-status d-flex align-items-center">
          <div className="d-flex align-items-center gap-4 justify-content-between w-100">
            <label className="radio-wrapper">
              <input
                type="checkbox"
                value="Fixed Deposit"
                {...register("accountType", {
                  required: "Please select at least one option",
                })}
              />
              <span className="custom-radio"></span>
              Fixed Deposit
            </label>

            <label className="radio-wrapper">
              <input
                type="checkbox"
                value="Savings Account"
                {...register("accountType", {
                  required: "Please select at least one option",
                })}
              />
              <span className="custom-radio"></span>
              Savings Account
            </label>
          </div>

          {errors.accountType && (
            <span className="error">{errors.accountType.message}</span>
          )}
        </div>

        {/* Terms */}
        <div className="terms input_wrapper d-flex gap-2 mt-3 align-items-end">
          <Controller
            name="agreetnc"
            control={control}
            defaultValue={false}
            rules={{
              validate: (value) =>
                value === true || "Agree Terms and Condition",
            }}
            render={({ field }) => (
              <input
                type="checkbox"
                id="agreetnc"
                style={{ width: "fit-content" }}
                checked={field.value}
                onChange={(e) => {
                  field.onChange(e.target.checked);
                  ctaClickEvent(
                    `consent checkbox: ${e.target.checked}`,
                    "lead form",
                    "checkbox",
                    0,
                    "na",
                  );
                }}
              />
            )}
          />
          <label htmlFor="agreetnc">
            I authorise ICICI Bank and its representatives to contact me through
            phone, email, SMS and WhatsApp. I also agree to{" "}
            <a
              target="_blank"
              href="https://www.icici.bank.in/content/dam/icicibank/india/managed-assets/docs/terms-condition/tnc-savings-account.pdf"
              onClick={() => {
                handleFormStart();
                ctaClickEvent(
                  "terms & conditions",
                  "lead form",
                  "text link",
                  0,
                  "https://www.icici.bank.in/content/dam/icicibank/india/managed-assets/docs/terms-condition/tnc-savings-account.pdf",
                );
              }}
              onContextMenu={() => {
                handleFormStart();
                ctaClickEvent(
                  "terms & conditions",
                  "lead form",
                  "text link",
                  0,
                  "https://www.icici.bank.in/content/dam/icicibank/india/managed-assets/docs/terms-condition/tnc-savings-account.pdf",
                );
              }}
            >
              ICICI Bank’s Terms & Conditions.
            </a>
          </label>
        </div>
        {errors.agreetnc && (
          <span className="error">{errors.agreetnc.message}</span>
        )}

        <button type="submit" className="proceed">
          Request Call Back
        </button>

        <div id="toastContainer" className="toast-container" />
      </div>
    </form>
  );
}

export default FormComponent;
